import i18n from '@/utils/i18n';
import React, { useEffect, useRef } from 'react';
import {
  BookmarkAction,
  GrossMarginEditAction,
  PriceEditAction,
  RevertEditAction,
} from '@/experiences/sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import { createStyles } from '@/contexts/theme-context';
import { GestureResponderEvent, Pressable, View } from 'react-native';
import { KeyboardShortcutBadge } from '@sales-portal/components/keyboard-shortcut-badge';
import { Popover } from '@usepepper/sprout-kit-ui';
import { Typography } from '@usepepper/ui/atoms/typography';

export const QUICK_ACTIONS_SHORTCUT_KEY = 'Q';
const QUICK_ACTIONS_MENU_WIDTH = 232;

export enum QuickActionShortcutKey {
  A = 'A',
  B = 'B',
  L = 'L',
  R = 'R',
  S = 'S',
  X = 'X',
  Z = 'Z',
}

export interface PricingTableQuickAction {
  disabled?: boolean;
  id:
    | BookmarkAction
    | GrossMarginEditAction
    | PriceEditAction
    | RevertEditAction;
  label: string;
  onSelect: () => void;
  shortcutKey: QuickActionShortcutKey;
  valueLabel?: string;
}

export const getQuickActionMetadata = (
  id: PricingTableQuickAction['id'],
): Pick<PricingTableQuickAction, 'id' | 'label' | 'shortcutKey'> => {
  switch (id) {
    case GrossMarginEditAction.AddOnePercent:
      return {
        id,
        label: i18n.t(
          'pricing.table.quick_actions.gross_margin.add_one_percent',
        ),
        shortcutKey: QuickActionShortcutKey.A,
      };
    case GrossMarginEditAction.SubtractOnePercent:
      return {
        id,
        label: i18n.t(
          'pricing.table.quick_actions.gross_margin.subtract_one_percent',
        ),
        shortcutKey: QuickActionShortcutKey.S,
      };
    case PriceEditAction.EndInX9:
      return {
        id,
        label: i18n.t('pricing.table.quick_actions.price.end_in_x9'),
        shortcutKey: QuickActionShortcutKey.X,
      };
    case PriceEditAction.RandomRoundUp:
      return {
        id,
        label: i18n.t('pricing.table.quick_actions.price.random_round_up'),
        shortcutKey: QuickActionShortcutKey.Z,
      };
    case PriceEditAction.SetLastPrice:
      return {
        id,
        label: i18n.t('pricing.table.quick_actions.price.last_price'),
        shortcutKey: QuickActionShortcutKey.L,
      };
    case GrossMarginEditAction.SetLastMargin:
      return {
        id,
        label: i18n.t('pricing.table.quick_actions.gross_margin.last_margin'),
        shortcutKey: QuickActionShortcutKey.L,
      };
    case RevertEditAction.RevertEdit:
      return {
        id,
        label: i18n.t('pricing.table.quick_actions.revert'),
        shortcutKey: QuickActionShortcutKey.R,
      };
    default:
      throw new Error(`No quick-action metadata for ${id}`);
  }
};

interface Props {
  actions?: PricingTableQuickAction[];
  isOpen: boolean;
  onActionPressIn?: (event: GestureResponderEvent) => void;
  onActionSelect: (action: PricingTableQuickAction) => void;
  onOpenChange: (open: boolean) => void;
  // Omit to hide the "Apply to N selected" header (single-row quick actions).
  selectedCount?: number;
  trigger: React.ReactNode;
}

export const PricingTableQuickActionsPopover: React.FC<Props> = ({
  actions,
  isOpen,
  onActionPressIn,
  onActionSelect,
  onOpenChange,
  selectedCount,
  trigger,
}) => {
  const [styles, theme] = useStyles();
  const lastOpenActionsRef = useRef<PricingTableQuickAction[] | undefined>(
    actions,
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    lastOpenActionsRef.current = actions;
  }, [actions, isOpen]);

  const renderedActions = isOpen
    ? actions
    : (lastOpenActionsRef.current ?? actions);

  return (
    <Popover
      allowFlip
      offset={4}
      onOpenChange={onOpenChange}
      open={Boolean(isOpen && renderedActions?.length)}
      placement="top-start"
    >
      <Popover.Anchor>{trigger}</Popover.Anchor>
      <Popover.Content disableFocusScope>
        <View style={styles.quickActionsMenu}>
          <View style={styles.quickActionsContent}>
            {selectedCount !== undefined ? (
              <View style={styles.quickActionsHeader}>
                <Typography
                  color={theme.sk.color.text.secondary}
                  variant="paragraph-sm"
                >
                  {i18n.t('pricing.table.quick_actions.apply_to_selected', {
                    count: selectedCount,
                  })}
                </Typography>
              </View>
            ) : null}
            {renderedActions?.map((action) => {
              const isDisabled = Boolean(action.disabled);

              return (
                <Pressable
                  disabled={isDisabled}
                  key={action.id}
                  onPress={() => onActionSelect(action)}
                  onPressIn={onActionPressIn}
                  style={({ hovered }) => [
                    styles.quickActionRow,
                    hovered ? styles.quickActionRowHighlighted : null,
                  ]}
                >
                  <View style={styles.quickActionLeft}>
                    {action.shortcutKey ? (
                      <KeyboardShortcutBadge
                        shortcut={action.shortcutKey}
                        size={theme.sk.typography.paragraph.sm.lineHeight}
                      />
                    ) : null}
                    <Typography
                      color={
                        isDisabled
                          ? theme.sk.color.text.disabled
                          : theme.sk.color.text.primary
                      }
                      variant="paragraph-md"
                    >
                      {action.label}
                    </Typography>
                  </View>
                  {action.valueLabel ? (
                    <Typography
                      color={
                        isDisabled
                          ? theme.sk.color.text.disabled
                          : theme.sk.color.text.secondary
                      }
                      variant="paragraph-md"
                    >
                      {action.valueLabel}
                    </Typography>
                  ) : null}
                </Pressable>
              );
            })}
          </View>
        </View>
      </Popover.Content>
    </Popover>
  );
};

const useStyles = createStyles((theme) => ({
  quickActionLeft: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  quickActionRow: {
    alignItems: 'center',
    borderRadius: theme.sk.radii.radiusSmall,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  quickActionRowHighlighted: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  quickActionsContent: {
    width: '100%',
  },
  quickActionsHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    marginBottom: theme.sk.spacing.spaceXsmall,
    paddingHorizontal: theme.sk.spacing.spaceXsmall,
  },
  quickActionsMenu: {
    padding: theme.sk.spacing.spaceSmall,
    width: QUICK_ACTIONS_MENU_WIDTH,
  },
}));
